import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { MAX_INPUT_LENGTH } from '../../../../../../constants';
import styles from './PatientComplains.module.scss';
import { ErrorModal } from './components/ErrorModal/ErrorModal';
import cx from 'classnames';
import { Button, Icon, Text, Textarea } from 'components';
import { RolePermissionChecker } from 'components';
import { appActions } from 'store/app/slice';
import { selectCurrentPatientCase } from 'store/patients/selectors';
import { patientsActions } from 'store/patients/slice';
import { useDispatch, useSelector } from 'store/utils';
import { TreatmentPlanTask, UserRole } from 'types';
import { onlyLatinLettersSymbolsNumbers } from 'utils';

type Props = {
  id: number;
  task: TreatmentPlanTask;
  isBlocked: boolean;
};

const schema = yup.object({
  complain: yup
    .string()
    .matches(onlyLatinLettersSymbolsNumbers, {
      message: 'Only Latin Alphabet is allowed',
    })
    .required('The field is empty'),
});

const allowToEditTask = [TreatmentPlanTask.TO_SUBMIT];

export const PatientComplain = ({ id, task, isBlocked }: Props) => {
  const [complainEditMode, setComplainEditMode] = useState(false);
  const [displayError, setDisplayError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();
  const patientComplaint = useSelector(selectCurrentPatientCase)?.patientComplaint;

  const {
    setValue,
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    setComplainEditMode(false);
    setValue('complain', patientComplaint ?? '');
    clearErrors('complain');
  }, [patientComplaint, setValue]);

  const watchedComplain = watch('complain');

  useEffect(() => {
    return () => {
      dispatch(patientsActions.setShouldPatientPageUpdate(true));
      dispatch(patientsActions.setIsPatientPageEdited(false));
    };
  }, []);

  const onEditHandler = () => {
    setComplainEditMode(true);
    dispatch(patientsActions.setShouldPatientPageUpdate(false));
    dispatch(patientsActions.setIsPatientPageEdited(true));
  };
  const onCancelHandler = () => {
    setComplainEditMode(false);
    dispatch(patientsActions.setShouldPatientPageUpdate(true));
    dispatch(patientsActions.setIsPatientPageEdited(false));
  };

  const onSubmit = handleSubmit(async (formData) => {
    if (isLoading) return;
    try {
      setIsLoading(true);
      const wrappedResult = dispatch(
        patientsActions.updatePatientCase({
          patientCaseData: { patientComplaint: formData.complain },
          patientCaseId: id,
        }),
      );
      await wrappedResult.unwrap();
      dispatch(patientsActions.setShouldPatientPageUpdate(true));
      dispatch(patientsActions.setIsPatientPageEdited(false));
      setComplainEditMode(false);
      dispatch(appActions.setModalErrorText({ name: 'patientComplainError', errorText: '' }));
    } catch (error: any) {
      dispatch(appActions.setModalErrorText({ name: 'patientComplainError', errorText: error?.title }));
      setDisplayError(true);
      setComplainEditMode(false);
    } finally {
      setIsLoading(false);
    }
  });

  const isAllowedToEdit = !isBlocked && allowToEditTask.includes(task);

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.complainWrapper}>
          <div className={styles.complainBlock}>
            <Text type="overline" className={styles.upperCaseText}>
              Presenting Complaint
            </Text>
            {!complainEditMode && (
              <Text type="caption" className={styles.textNotSet}>
                {patientComplaint ? patientComplaint : 'Please provide patient complaints'}
              </Text>
            )}
          </div>
          {isAllowedToEdit && (
            <RolePermissionChecker rolesAllowedToView={[UserRole.CLINIC]}>
              {!complainEditMode && (
                <div className={cx(styles.iconWrapper, { [styles.white]: !!patientComplaint })} onClick={onEditHandler}>
                  {<Icon name={patientComplaint ? 'Edit' : 'Plus'} />}
                </div>
              )}
            </RolePermissionChecker>
          )}
        </div>
        {complainEditMode && (
          <form className={styles.inputItem}>
            <Textarea
              labelText=""
              placeholder="Please provide patient complaints"
              inputName="complain"
              register={register}
              maxLength={MAX_INPUT_LENGTH}
              autoComplete="off"
              characterCounter
              errorMsg={errors.complain?.message}
              currentValue={watchedComplain}
              setValue={setValue}
              isForLinks={false}
            />
            <div className={styles.buttonRow}>
              <Button type="secondary" onClick={onCancelHandler}>
                Cancel
              </Button>
              <Button onClick={onSubmit}>Save</Button>
            </div>
          </form>
        )}
      </div>
      {displayError && <ErrorModal onClose={() => setDisplayError(false)} />}
    </>
  );
};
